<template>
  <modal
    :show="isVisible"
    :title="modalTitle"
    elem-id="modal_site_unidades"
    size="large"
    :close-label="t('Cancelar')"
    :save-label="isDeleteMode ? (isDeleting ? t('Excluindo') : t('Excluir')) : (isSaving ? t('Salvando') : t('Salvar'))"
    :save-fn="isDeleteMode ? handleDelete : handleSave"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <!-- Delete Confirmation Mode -->
      <div v-if="isDeleteMode" class="delete-confirmation" data-testid="site-unidades-delete-confirmation">
        <p>{{ t('Tem certeza que deseja excluir esta unidade? Esta ação não pode ser desfeita.') }}</p>
        <p class="text-muted">{{ unidadeToDelete?.nome?.['pt-BR'] || unidadeToDelete?.slug }}</p>
      </div>

      <!-- Create/Edit Mode -->
      <div v-else>
        <site-unidades-form
          ref="formRef"
          :unidadeId="currentUnidadeId"
          :isEditMode="isEditMode"
          @submit-success="handleFormSuccess"
          @submit-error="handleFormError"
          @validation-failed="handleValidationFailed"
        />
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSiteUnidades } from '../../composables/useSiteUnidades';
import { eventBus } from '../../eventBus';
import SiteUnidadesForm from './SiteUnidadesForm.vue';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type UnidadeId = number | string;

interface UnidadeRecord {
  id?: number;
  slug?: string;
  nome?: Record<string, string>;
  [key: string]: unknown;
}

interface SiteUnidadesFormInstance {
  resetForm: () => void;
  loadForEdit: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  validateForm: () => boolean;
}

interface ModalOpenPayload {
  id?: UnidadeId;
  action?: 'create' | 'edit' | 'delete';
  data?: UnidadeRecord | null;
}

const { t } = useI18n();

const { excluirUnidade } = useSiteUnidades();

const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const isDeleteMode = ref<boolean>(false);
const currentUnidadeId = ref<UnidadeId | null>(null);
const unidadeToDelete = ref<UnidadeRecord | null>(null);
const formRef = ref<SiteUnidadesFormInstance | null>(null);
const isSaving = ref<boolean>(false);
const isDeleting = ref<boolean>(false);

const modalTitle = computed<string>(() => {
  if (isDeleteMode.value) {
    return t('Excluir Unidade');
  } else if (isEditMode.value) {
    return t('Editar Unidade');
  } else {
    return t('Adicionar Unidade');
  }
});

const openForCreate = (): void => {
  log('SiteUnidadesModal', 'openForCreate', 'Opening modal for create');
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentUnidadeId.value = null;

  nextTick(() => {
    if (formRef.value) {
      formRef.value.resetForm();
    }
  });
};

const openForEdit = async (unidadeId: UnidadeId): Promise<void> => {
  log('SiteUnidadesModal', 'openForEdit', 'Opening modal for edit', unidadeId);
  isVisible.value = true;
  isEditMode.value = true;
  isDeleteMode.value = false;
  currentUnidadeId.value = unidadeId;

  await nextTick();

  if (formRef.value) {
    await formRef.value.loadForEdit();
  }
};

const openForDelete = (unidadeId: UnidadeId, unidadeData: UnidadeRecord | null = null): void => {
  log('SiteUnidadesModal', 'openForDelete', 'Opening modal for delete', unidadeId);
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = true;
  currentUnidadeId.value = unidadeId;
  unidadeToDelete.value = unidadeData;
};

const handleClose = (): void => {
  log('SiteUnidadesModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentUnidadeId.value = null;
  unidadeToDelete.value = null;
  isSaving.value = false;
  isDeleting.value = false;

  if (formRef.value) {
    formRef.value.resetForm();
  }
};

const handleSave = async (): Promise<void> => {
  log('SiteUnidadesModal', 'handleSave', 'Save button clicked');
  if (formRef.value) {
    isSaving.value = true;
    try {
      await formRef.value.handleSubmit();
    } finally {
      isSaving.value = false;
    }
  }
};

const handleDelete = async (): Promise<void> => {
  if (!currentUnidadeId.value) {
    logWarn('SiteUnidadesModal', 'handleDelete', 'No unit ID to delete');
    return;
  }

  log('SiteUnidadesModal', 'handleDelete', 'Deleting unit', currentUnidadeId.value);
  isDeleting.value = true;

  try {
    const idAsNumber = typeof currentUnidadeId.value === 'string'
      ? parseInt(currentUnidadeId.value, 10)
      : currentUnidadeId.value;
    await excluirUnidade(idAsNumber);
    eventBus.emit('globalMessage.success', t('Unidade excluída com sucesso'));
    eventBus.emit('site-unidades.deleted');
    handleClose();
  } catch (err) {
    const error = err as Error;
    logError('SiteUnidadesModal', 'handleDelete', 'Error deleting unit', error);
    eventBus.emit('globalMessage.error', t('Erro ao excluir unidade') + ': ' + error.message);
  } finally {
    isDeleting.value = false;
  }
};

const handleFormSuccess = (): void => {
  log('SiteUnidadesModal', 'handleFormSuccess', 'Form submitted successfully');
  eventBus.emit('site-unidades.saved');
  handleClose();
};

const handleFormError = (error: unknown): void => {
  logError('SiteUnidadesModal', 'handleFormError', 'Form submission error', error);
};

const handleValidationFailed = (): void => {
  logWarn('SiteUnidadesModal', 'handleValidationFailed', 'Client-side validation failed');
};

const handleModalOpen = (data?: ModalOpenPayload): void => {
  log('SiteUnidadesModal', 'handleModalOpen', 'Standard modal open event received', data);

  if (data?.action === 'delete' && data?.id) {
    openForDelete(data.id, data.data || null);
  } else if (data?.id) {
    void openForEdit(data.id);
  } else {
    openForCreate();
  }
};

onMounted(() => {
  log('SiteUnidadesModal', 'onMounted', 'Component mounted, setting up event listeners');
  eventBus.on('modal.site-unidades.open', handleModalOpen);
  eventBus.on('modal.site-unidades.close', handleClose);
});

onBeforeUnmount(() => {
  log('SiteUnidadesModal', 'onBeforeUnmount', 'Component unmounting, cleaning up event listeners');
  eventBus.off('modal.site-unidades.open', handleModalOpen);
  eventBus.off('modal.site-unidades.close', handleClose);
});

defineExpose({
  openForCreate,
  openForEdit,
  openForDelete
});
</script>

<style scoped>
.delete-confirmation {
  padding: 20px;
  text-align: center;
}

.delete-confirmation p {
  margin-bottom: 10px;
  font-size: 16px;
}

.text-muted {
  color: #6c757d;
  font-style: italic;
}
</style>
